import { gql } from "@apollo/client";
import { getCachedCategory } from "./categories";
import { buildPath, matchCityWithDirection } from "../../lib/helpers";
import { TYPES_KEYFIELD_MAP, URL_LEVELS } from "../../common/constants";
import staticRegions from "../../common/regions";

export const GET_REGIONS = gql`
  query getRegions {
    Web {
      regions {
        district {
          district_id
          name
          uri
          path
        }
        direction {
          direction_id
          name
          uri
          path
        }
        city {
          city_id
          name
          uri
          path
        }
      }
    }
  }
`;

export const GET_ALL_CITIES = gql`
  query getAllCities($category: Int!) {
    Web {
      cities(category: $category) {
        name
        title
        uri
        path
        city_id
        count
        category {
          name
          plural
          uri
          path
          keywords
          id
        }
        directions
      }
    }
  }
`;

export const GET_AHLI_CITIES = gql`
  query {
    BankLoans {
      getCities {
        key
        description
      }
    }
  }
`;

export const GET_ALL_DIRECTIONS = gql`
  query getAllDirections($category: Int!, $city_id: Int!) {
    Web {
      directions(category: $category, city_id: $city_id) {
        name
        title
        uri
        path
        count
        category {
          name
          uri
          id
        }
        direction_id
        city {
          uri
          city_id
        }
      }
    }
  }
`;

export const GET_ALL_DISTRICTS = gql`
  query getAllDistricts($category: Int!, $city_id: Int!, $direction_id: Int) {
    Web {
      districts(
        category: $category
        city_id: $city_id
        direction_id: $direction_id
      ) {
        name
        title
        uri
        path
        count
        district_id
        category {
          name
          uri
          id
        }
        city {
          uri
          city_id
        }
        direction {
          uri
          direction_id
        }
      }
    }
  }
`;

export const getCachedObject = (regions, typename, keyFieldValue) => {
  const regionsCache = regions || staticRegions;
  // eslint-disable-next-line no-use-before-define
  const id = getCanonicalRef(typename, keyFieldValue);
  return regionsCache ? regionsCache[id] : undefined;
};

export const getRegionNextLevelQuery = (pathLevel, pathParams) => {
  pathLevel = pathLevel || {};
  switch (pathLevel.level) {
    case URL_LEVELS.CATEGORY:
      return GET_ALL_CITIES;
    case URL_LEVELS.CITY:
      if (matchCityWithDirection(pathParams[0])) {
        return GET_ALL_DIRECTIONS;
      }
      return GET_ALL_DISTRICTS;
    case URL_LEVELS.DIRECTION:
      return GET_ALL_DISTRICTS;
    case URL_LEVELS.DISTRICT:
      return GET_ALL_DISTRICTS;
    default:
      return GET_ALL_CITIES;
  }
};

export const getSpecialListingslVars = (
  categoryName,
  pathLevel,
  apolloClient
) => {
  pathLevel = pathLevel || {};

  const { cityName } = pathLevel;

  const { id: categoryId } =
    getCachedCategory(apolloClient, categoryName) || {};
  const { city_id: cityId } =
    getCachedObject(staticRegions, "CityType", cityName) || {};
  const districtPath = `/${buildPath({
    path: "{cityName}/{directionName}/{districtName}",
    pathParams: pathLevel,
  })}`;
  const { district_id: districtId } =
    getCachedObject(staticRegions, "DistrictType", districtPath) || {};

  return {
    ...((categoryId || categoryId === 0) && {
      category: categoryId,
    }),
    ...(cityId && {
      city_id: cityId,
    }),
    ...(districtId && {
      district_id: districtId,
    }),
  };
};

export const getRegionLevelVars = (
  categoryName,
  pathLevel,
  apolloClient,
  regions
) => {
  pathLevel = pathLevel || {};

  const { cityName, directionName } = pathLevel;

  const { id: categoryId } =
    getCachedCategory(apolloClient, categoryName) || {};
  const { city_id: cityId } =
    getCachedObject(regions, "CityType", cityName) || {};
  const { direction_id: directionId } =
    getCachedObject(regions, "DirectionType", directionName) || {};
  return {
    ...((categoryId || categoryId === 0) && {
      category: categoryId,
    }),
    ...(cityId && {
      city_id: cityId,
    }),
    ...(directionId && {
      direction_id: directionId,
    }),
  };
};

export const getRegionList = (regionsData, pathLevel, pathParams) => {
  const { Web = {} } = regionsData || {};
  pathLevel = pathLevel || {};
  switch (pathLevel.level) {
    case URL_LEVELS.CATEGORY:
      return Web.cities || [];
    case URL_LEVELS.CITY:
      if (matchCityWithDirection(pathParams[0])) {
        return Web.directions || [];
      }
      return Web.districts;
    case URL_LEVELS.DIRECTION:
      return Web.districts || [];
    case URL_LEVELS.DISTRICT:
      return Web.districts || [];
    default:
      return Web.cities || [];
  }
};

export const purgeRegionsCache = (client) => {
  client.cache.modify({
    id: "ROOT_QUERY",
    fields: {
      Web({ regions, ...rest }) {
        return rest;
      },
    },
  });

  client.cache.gc();
};

/**
 * returns the key of the specified cached object given it is typename and the
 * value of the key field (as it is set in the apollo client InMemoryCache)
 * @param typename
 * @param id
 * @return {string}
 */
function getCanonicalRef(typename, id) {
  const keyField = TYPES_KEYFIELD_MAP[typename];
  return `${typename}:${JSON.stringify({ [keyField]: id })}`;
}
